import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vehicleAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Plus, Car, Trash2, Star, Search, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const MyGarage = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [addingVehicle, setAddingVehicle] = useState(false);
  
  const emptyForm = { registration: '', make: '', model: '', year: '', color: '', fuel_type: '', nickname: '' };
  const [form, setForm] = useState(emptyForm);
  const [lookupAvailable, setLookupAvailable] = useState(false);

  const setField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  useEffect(() => {
    fetchVehicles();
    vehicleAPI.isLookupAvailable().then(setLookupAvailable).catch(() => setLookupAvailable(false));
  }, []);

  const fetchVehicles = async () => {
    try {
      const data = await vehicleAPI.getVehicles();
      setVehicles(data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast({
        title: 'Error',
        description: 'Failed to load vehicles',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = async () => {
    if (!form.registration) {
      toast({
        title: 'Error',
        description: 'Please enter a registration number',
        variant: 'destructive'
      });
      return;
    }

    setLookupLoading(true);
    try {
      const data = await vehicleAPI.lookupVehicle(form.registration);
      // DVLA never returns a model, so the customer always types that in.
      setForm({
        ...form,
        make: data.make || form.make,
        year: data.year || form.year,
        color: data.color || form.color,
        fuel_type: data.fuel_type || form.fuel_type
      });
      toast({
        title: 'Vehicle Found!',
        description: 'Check the details and add the model.'
      });
    } catch (error) {
      toast({
        title: 'Lookup Failed',
        description: error.response?.data?.detail || 'Vehicle not found. Please enter the details manually.',
        variant: 'destructive'
      });
    } finally {
      setLookupLoading(false);
    }
  };

  const handleAddVehicle = async () => {
    if (!form.registration || !form.make.trim() || !form.model.trim()) {
      toast({
        title: 'Missing details',
        description: 'Please enter the registration, make and model',
        variant: 'destructive'
      });
      return;
    }

    setAddingVehicle(true);
    try {
      await vehicleAPI.addVehicle(form);
      toast({
        title: 'Success',
        description: 'Vehicle added to your garage'
      });
      setIsDialogOpen(false);
      setForm(emptyForm);
      fetchVehicles();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add vehicle',
        variant: 'destructive'
      });
    } finally {
      setAddingVehicle(false);
    }
  };

  const handleSetPrimary = async (vehicleId) => {
    try {
      await vehicleAPI.setPrimaryVehicle(vehicleId);
      toast({
        title: 'Success',
        description: 'Primary vehicle updated'
      });
      fetchVehicles();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to set primary vehicle',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteVehicle = async (vehicleId, vehicleName) => {
    if (!window.confirm(`Are you sure you want to remove ${vehicleName}?`)) {
      return;
    }

    try {
      await vehicleAPI.deleteVehicle(vehicleId);
      toast({
        title: 'Success',
        description: 'Vehicle removed from garage'
      });
      fetchVehicles();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete vehicle',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-wheely-navy-950">
      {/* Header */}
      <div className="bg-gradient-to-r from-wheely-navy-950 to-wheely-navy-900 border-b border-wheely-orange/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/customer/dashboard')}
            className="text-wheely-orange hover:text-wheely-orange-light hover:bg-wheely-orange/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">My Garage</h2>
            <p className="text-gray-400">Manage your vehicles</p>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-wheely-orange to-wheely-orange-dark hover:from-wheely-orange-dark hover:to-orange-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Vehicle
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-wheely-navy-950 border-wheely-orange/20 text-white">
              <DialogHeader>
                <DialogTitle className="text-2xl">Add Vehicle</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Enter your vehicle's details so drivers know what they're coming to
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="registration">Registration Number *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="registration"
                      placeholder="e.g., AB12 CDE"
                      value={form.registration}
                      onChange={(e) => setForm({ ...form, registration: e.target.value.toUpperCase() })}
                      className="bg-wheely-navy-900 border-wheely-navy-700 text-white flex-1"
                    />
                    {lookupAvailable && (
                      <Button
                        onClick={handleLookup}
                        disabled={lookupLoading}
                        className="bg-blue-600 hover:bg-blue-700"
                        title="Look up with the DVLA"
                      >
                        {lookupLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="make">Make *</Label>
                    <Input
                      id="make"
                      placeholder="e.g., Ford"
                      value={form.make}
                      onChange={setField('make')}
                      className="bg-wheely-navy-900 border-wheely-navy-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="model">Model *</Label>
                    <Input
                      id="model"
                      placeholder="e.g., Focus"
                      value={form.model}
                      onChange={setField('model')}
                      className="bg-wheely-navy-900 border-wheely-navy-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="year">Year</Label>
                    <Input
                      id="year"
                      placeholder="e.g., 2018"
                      value={form.year}
                      onChange={setField('year')}
                      inputMode="numeric"
                      maxLength={4}
                      className="bg-wheely-navy-900 border-wheely-navy-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="color">Colour</Label>
                    <Input
                      id="color"
                      placeholder="e.g., Silver"
                      value={form.color}
                      onChange={setField('color')}
                      className="bg-wheely-navy-900 border-wheely-navy-700 text-white"
                    />
                  </div>
                </div>

                  <div className="space-y-2">
                    <Label htmlFor="fuel_type">Fuel Type</Label>
                    <Input
                      id="fuel_type"
                      placeholder="e.g., Petrol, Diesel, Electric"
                      value={form.fuel_type}
                      onChange={setField('fuel_type')}
                      className="bg-wheely-navy-900 border-wheely-navy-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nickname">Nickname (Optional)</Label>
                    <Input
                      id="nickname"
                      placeholder="e.g., My Daily Driver"
                      value={form.nickname}
                      onChange={setField('nickname')}
                      className="bg-wheely-navy-900 border-wheely-navy-700 text-white"
                    />
                  </div>

                <Button
                  onClick={handleAddVehicle}
                  disabled={addingVehicle}
                  className="w-full bg-gradient-to-r from-wheely-orange to-wheely-orange-dark hover:from-wheely-orange-dark hover:to-orange-700"
                >
                  {addingVehicle ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add to Garage'
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Vehicles List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-wheely-orange border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-400 mt-4">Loading vehicles...</p>
          </div>
        ) : vehicles.length === 0 ? (
          <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
            <CardContent className="py-12 text-center">
              <Car className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Vehicles Yet</h3>
              <p className="text-gray-400 mb-6">Add your first vehicle to get started</p>
              <Button
                onClick={() => setIsDialogOpen(true)}
                className="bg-gradient-to-r from-wheely-orange to-wheely-orange-dark hover:from-wheely-orange-dark hover:to-orange-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Vehicle
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {vehicles.map((vehicle) => (
              <Card
                key={vehicle.id}
                className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20 hover:border-wheely-orange/50 transition-colors"
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="bg-wheely-orange/10 p-3 rounded-xl">
                          <Car className="w-6 h-6 text-wheely-orange" />
                        </div>
                        <div>
                          <CardTitle className="text-white text-xl flex items-center gap-2">
                            {vehicle.nickname || `${vehicle.make} ${vehicle.model}`}
                            {vehicle.is_primary && (
                              <Badge className="bg-wheely-orange text-white">
                                <Star className="w-3 h-3 mr-1" />
                                Primary
                              </Badge>
                            )}
                          </CardTitle>
                          <CardDescription className="text-gray-400">
                            {vehicle.registration}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-400 text-sm">Make:</span>
                        <span className="text-white font-semibold text-sm">{vehicle.make}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 text-sm">Model:</span>
                        <span className="text-white font-semibold text-sm">{vehicle.model}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-400 text-sm">Year:</span>
                        <span className="text-white font-semibold text-sm">{vehicle.year || '—'}</span>
                      </div>
                      {vehicle.color && (
                        <div className="flex justify-between">
                          <span className="text-gray-400 text-sm">Color:</span>
                          <span className="text-white font-semibold text-sm">{vehicle.color}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2 border-t border-wheely-navy-700">
                    {!vehicle.is_primary && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSetPrimary(vehicle.id)}
                        className="flex-1 border-wheely-orange/50 text-wheely-orange hover:bg-wheely-orange/10"
                      >
                        <Star className="w-4 h-4 mr-2" />
                        Set as Primary
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteVehicle(vehicle.id, vehicle.nickname || vehicle.model)}
                      className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyGarage;
